import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TOOLS_CATALOG, STAGE_TOOLS, SOP_STAGE_LABELS } from '@/lib/sopConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_ORDER = ['00', '01', '02', '03', '04'];

const formatLabel = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const ToolsPanel = ({ projectId }) => {
  const [activeStage, setActiveStage] = useState('00');
  // selections[stage][category] = chosen tool name
  const [selections, setSelections] = useState({});
  // customTools[stage][category] = freetext when "Other" is selected
  const [customTools, setCustomTools] = useState({});
  const [catalog, setCatalog] = useState(TOOLS_CATALOG);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    const init = async () => {
      setLoading(true);
      try {
        // Try remote catalog first; fall back to local constant silently
        try {
          const catRes = await axios.get(`${API}/tools-catalog`, { withCredentials: true });
          if (catRes.data && typeof catRes.data === 'object' && !Array.isArray(catRes.data)) {
            setCatalog(catRes.data);
          }
        } catch { /* use TOOLS_CATALOG local constant */ }

        // Load saved selections
        const res = await axios.get(`${API}/projects/${projectId}/tools`, { withCredentials: true });
        const selMap = {};
        const custMap = {};
        (Array.isArray(res.data) ? res.data : []).forEach(item => {
          if (!selMap[item.stage]) selMap[item.stage] = {};
          if (!custMap[item.stage]) custMap[item.stage] = {};
          selMap[item.stage][item.category] = item.tool || '';
          custMap[item.stage][item.category] = item.custom_tool || '';
        });
        setSelections(selMap);
        setCustomTools(custMap);
      } catch {
        // Not fatal — selections start empty, user can fill in
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [projectId]);

  const getSelection = (stage, category) => selections[stage]?.[category] || '';
  const getCustom = (stage, category) => customTools[stage]?.[category] || '';

  const setSelection = (stage, category, value) => {
    setSelections(prev => ({
      ...prev,
      [stage]: { ...(prev[stage] || {}), [category]: value },
    }));
  };

  const setCustom = (stage, category, value) => {
    setCustomTools(prev => ({
      ...prev,
      [stage]: { ...(prev[stage] || {}), [category]: value },
    }));
  };

  const handleSave = async (stage) => {
    setSaving(true);
    try {
      const categories = STAGE_TOOLS[stage] || [];
      const payload = categories.map(category => ({
        stage,
        category,
        tool: getSelection(stage, category),
        custom_tool: getSelection(stage, category) === 'Other'
          ? (getCustom(stage, category) || null)
          : null,
      }));
      await axios.patch(`${API}/projects/${projectId}/tools`, payload, { withCredentials: true });
      toast.success(`Stage ${stage} tools saved`);
    } catch {
      toast.error('Failed to save tools');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6" data-testid="tools-panel">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-6">
            <h2 className="font-heading text-2xl text-foreground mb-2">Tools</h2>
            <p className="text-sm text-muted-foreground font-mono">
              Select the tools your team uses at each stage of the engagement
            </p>
          </div>

          {/* Stage tab bar */}
          <div className="flex border border-border mb-6" style={{ borderRadius: 0 }}>
            {STAGE_ORDER.map((stage, idx) => {
              const info = SOP_STAGE_LABELS[stage];
              const isActive = activeStage === stage;
              return (
                <button
                  key={stage}
                  onClick={() => setActiveStage(stage)}
                  className={`flex-1 px-2 py-3 font-mono text-xs transition-colors ${
                    idx < STAGE_ORDER.length - 1 ? 'border-r border-border' : ''
                  } ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-transparent text-muted-foreground hover:bg-black/[0.04] hover:text-foreground'
                  }`}
                  style={{ borderRadius: 0 }}
                  data-testid={`tools-stage-tab-${stage}`}
                >
                  <div className="font-semibold">{stage}</div>
                  <div className="opacity-70 text-[10px] mt-0.5 hidden sm:block truncate">
                    {info.label}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Tool categories for the active stage */}
          <div className="glass border border-border p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              {(STAGE_TOOLS[activeStage] || []).map(category => {
                const options = catalog[category] || [];
                const selected = getSelection(activeStage, category);
                const custom = getCustom(activeStage, category);

                return (
                  <div key={category} className="space-y-1.5">
                    <label
                      htmlFor={`tool-${activeStage}-${category}`}
                      className="block font-mono text-xs font-medium text-foreground/60 uppercase tracking-wide"
                    >
                      {formatLabel(category)}
                    </label>
                    <select
                      id={`tool-${activeStage}-${category}`}
                      value={selected}
                      onChange={e => setSelection(activeStage, category, e.target.value)}
                      className="w-full bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50"
                      style={{ borderRadius: 0 }}
                      data-testid={`tool-select-${category}`}
                    >
                      <option value="">— Select tool —</option>
                      {options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                    {selected === 'Other' && (
                      <input
                        type="text"
                        value={custom}
                        onChange={e => setCustom(activeStage, category, e.target.value)}
                        placeholder="Specify tool..."
                        className="w-full bg-black/[0.03] border border-border text-foreground text-sm px-3 py-2 font-mono focus:outline-none focus:ring-1 focus:ring-primary/50 placeholder:text-muted-foreground/60"
                        style={{ borderRadius: 0 }}
                        data-testid={`tool-custom-${category}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-border flex justify-end">
              <Button
                onClick={() => handleSave(activeStage)}
                disabled={saving}
                className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50 font-mono"
                style={{ borderRadius: 0 }}
                data-testid="tools-save-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />Save Stage {activeStage}</>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ToolsPanel;
